import { z } from 'zod';
import { AssistantError } from './assistant';
import { moneyMinorStringSchema } from './money';
import {
  catalogConfigurationSchema,
  type CatalogAttribute,
  type CatalogConfiguration,
} from './catalog-contracts';

const attribute = (
  key: string,
  label: string,
  type: CatalogAttribute['type'],
  unit: string | null = null
): CatalogAttribute => ({ key, label, type, unit });
export const PROPERTY_CATALOG_ATTRIBUTES: readonly CatalogAttribute[] = [
  attribute('description', 'Descripción', 'text'),
  attribute('unitType', 'Tipo de inmueble', 'text'),
  attribute('typology', 'Distribución', 'text'),
  attribute('floorNumber', 'Piso', 'number'),
  attribute('bedroomsCount', 'Dormitorios', 'number'),
  attribute('bathroomsCount', 'Baños', 'number'),
  attribute('parkingSpacesCount', 'Estacionamientos', 'number'),
  attribute('storageUnitsCount', 'Depósitos', 'number'),
  attribute('totalAreaM2', 'Área total', 'decimal', 'm²'),
  attribute('builtAreaM2', 'Área construida', 'decimal', 'm²'),
  attribute('pricePenMinor', 'Precio en soles', 'money', 'PEN'),
  attribute('priceUsdMinor', 'Precio en dólares', 'money', 'USD'),
  attribute('availability', 'Disponibilidad', 'text'),
  attribute('amenities', 'Características', 'text_list'),
  attribute('estimatedDeliveryDate', 'Fecha de entrega', 'text'),
  attribute('viviendaSocial', 'Marcador de vivienda social', 'boolean'),
];
export const GENERAL_CATALOG_ATTRIBUTES: readonly CatalogAttribute[] = [
  attribute('description', 'Descripción', 'text'),
  attribute('pricePenMinor', 'Precio en soles', 'money', 'PEN'),
  attribute('priceUsdMinor', 'Precio en dólares', 'money', 'USD'),
  attribute('availability', 'Disponibilidad', 'text'),
];

export function catalogProfile(
  kind: 'property' | 'general',
  configuration?: CatalogConfiguration
): CatalogConfiguration {
  if (configuration && configuration.profile !== kind)
    throw new AssistantError('CATALOG_PROFILE_MISMATCH', 409);
  const profile = configuration ?? {
    profile: kind,
    label: kind === 'property' ? 'Inmuebles' : 'Productos',
    referenceTerms:
      kind === 'property'
        ? [
            'departamento',
            'depa',
            'unidad',
            'apartment',
            'unit',
            'duplex',
            'dúplex',
            'triplex',
            'tríplex',
          ]
        : ['producto', 'product', 'modelo', 'model', 'sku'],
    attributes: [],
  };
  const attributes = profile.attributes.length
    ? profile.attributes
    : kind === 'property'
      ? PROPERTY_CATALOG_ATTRIBUTES
      : GENERAL_CATALOG_ATTRIBUTES;
  if (
    kind === 'property' &&
    attributes.some(
      (item) =>
        !PROPERTY_CATALOG_ATTRIBUTES.some(
          (supported) =>
            supported.key === item.key &&
            supported.type === item.type &&
            supported.unit === item.unit
        )
    )
  )
    throw new AssistantError('CATALOG_PROPERTY_ATTRIBUTE_UNSUPPORTED', 409);
  if (
    kind === 'property' &&
    !attributes.some((item) => item.key === 'availability')
  )
    throw new AssistantError('CATALOG_AVAILABILITY_REQUIRED', 409);
  if (
    attributes.some((item) =>
      [
        'id',
        'code',
        'title',
        'source',
        'version',
        'observedAt',
        'catalogProfile',
        'priceAmounts',
        'instruction',
        'search',
        'unverifiedCriteria',
        'evidenceScope',
        'unknownRequirements',
        'unknownAttributes',
        'conflictingAttributes',
        'attributeStatus',
        'evidenceNote',
        'currentPriceAndAvailability',
        '__proto__',
        'constructor',
        'prototype',
      ].includes(item.key)
    )
  )
    throw new AssistantError('CATALOG_ATTRIBUTE_RESERVED', 409);
  if (
    attributes.some(
      (item) =>
        item.type === 'money' && !['PEN', 'USD'].includes(item.unit ?? '')
    )
  )
    throw new AssistantError('CATALOG_MONEY_CURRENCY_REQUIRED', 409);
  const currencies = attributes
    .filter((item) => item.type === 'money')
    .map((item) => item.unit);
  if (new Set(currencies).size !== currencies.length)
    throw new AssistantError('CATALOG_MONEY_CURRENCY_AMBIGUOUS', 409);
  return catalogConfigurationSchema.parse({ ...profile, attributes });
}

export function catalogAttributeValue(
  attribute: CatalogAttribute,
  value: unknown
) {
  if (value === null || value === undefined) return null;
  const schemas = {
    text: z.string().max(8000),
    number: z.number().finite(),
    decimal: z.string().regex(/^-?\d+(?:\.\d+)?$/u),
    boolean: z.boolean(),
    text_list: z.array(z.string().max(500)).max(80),
    money: moneyMinorStringSchema,
  };
  const parsed = schemas[attribute.type].safeParse(value);
  if (!parsed.success)
    throw new AssistantError('CATALOG_ATTRIBUTE_VALUE_INVALID', 502);
  return parsed.data;
}
